/**
 * TaskDetailsFollowers Component
 *
 * Renders the followers card section including:
 * - Followers list with avatars
 * - Follow/unfollow toggle button
 * - Empty state message
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Tooltip, message } from 'antd';
import {
  EyeOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';

const TaskDetailsFollowers = ({
  currentTask,
  currentTaskId,
  currentUser,
  isFollowers,
  isCurrentUserAssignee,
  isCollaborator,
  updateTaskLoading,
  updateTask,
  refetchTask,
  onFollowChange,
}) => {
  const handleFollowToggle = async () => {
    const createdBy =
      currentUser.id ===
      (currentTask && currentTask.createdBy && currentTask.createdBy.id);

    if (!isFollowers && (isCurrentUserAssignee || isCollaborator || createdBy)) {
      if (isCurrentUserAssignee) {
        message.warning('Assignees already follow tasks by default.');
      } else if (isCollaborator) {
        message.warning('Collaborators already follow tasks by default.');
      } else if (createdBy) {
        message.warning(
          'You are the creator of this task and already follows it by default.'
        );
      }
      return;
    }

    onFollowChange(!isFollowers);

    if (!isFollowers) {
      try {
        await updateTask({
          variables: {
            data: {
              followers: {
                connect: { id: currentUser.id },
              },
            },
            where: { id: currentTaskId },
          },
        });
        refetchTask();
        message.success('You followed the task.');
      } catch (error) {
        message.error('Failed to follow.');
      }
    } else {
      try {
        await updateTask({
          variables: {
            data: {
              followers: {
                disconnect: { id: currentUser.id },
              },
            },
            where: { id: currentTaskId },
          },
        });
        refetchTask();
        message.success('Unfollowed task.');
      } catch (error) {
        message.error('Failed to unfollow task.');
      }
    }
  };

  return (
    <Card className="task-followers__card">
      <Col className="task-followers__header">
        <Col className="task-followers__title-row">
          <EyeOutlined className="task-followers__icon" />
          <Text strong className="task-followers__title">
            Followers
          </Text>
        </Col>
        <Button
          className="task-followers__toggle-btn"
          disabled={updateTaskLoading}
          onClick={handleFollowToggle}
        >
          {isFollowers ? (
            <>
              <MinusOutlined className="task-followers__action-icon task-followers__action-icon--unfollow" />
              <Text className="task-followers__action-text task-followers__action-text--unfollow">Unfollow</Text>
            </>
          ) : (
            <>
              <PlusOutlined className="task-followers__action-icon task-followers__action-icon--follow" />
              <Text className="task-followers__action-text task-followers__action-text--follow">Follow</Text>
            </>
          )}
        </Button>
      </Col>
      <Col>
        {currentTask && currentTask.followers && currentTask.followers.length > 0 ? (
          <>
            {currentTask.followers.map((follower, idx) => (
              <Tooltip
                key={follower.id || idx}
                title={startCase(follower.name || follower.username)}
              >
                <div className="task-followers__avatar-wrapper">
                  <AvatarS3
                    key={follower.id || idx}
                    imgKey={follower.avatar && follower.avatar.key}
                    username={follower.name || follower.username}
                    avatarProps={{ size: 40 }}
                  />
                </div>
              </Tooltip>
            ))}
          </>
        ) : (
          <Card className="task-followers__empty-card">
            <Text>
              Followers receive the same alerts and updates as the requestor without
              official responsibility.
            </Text>
          </Card>
        )}
      </Col>
    </Card>
  );
};

TaskDetailsFollowers.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    followers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      username: PropTypes.string,
      avatar: PropTypes.shape({
        key: PropTypes.string,
      }),
    })),
    createdBy: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  currentTaskId: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  isFollowers: PropTypes.bool,
  isCurrentUserAssignee: PropTypes.bool,
  isCollaborator: PropTypes.bool,
  updateTaskLoading: PropTypes.bool,
  updateTask: PropTypes.func.isRequired,
  refetchTask: PropTypes.func.isRequired,
  onFollowChange: PropTypes.func.isRequired,
};

TaskDetailsFollowers.defaultProps = {
  currentTask: null,
  isFollowers: false,
  isCurrentUserAssignee: false,
  isCollaborator: false,
  updateTaskLoading: false,
};

export default TaskDetailsFollowers;
